import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { RecordingState, RecordingView } from "@typedoc/contracts/recordings";
import { openQuestions, replay } from "@typedoc/interview/session";
import { Button, buttonVariants } from "@typedoc/ui";
import { hitTimeLabel, timeLabel } from "../../lib/chats/view";
import { recordingTapeQueryOptions } from "../../lib/queries/recordings";
import { canRecordHere, isGoing } from "../../lib/recordings/chat-recordings";
import { desktopShowIsland } from "../../lib/recordings/desktop";
import { ChevronRightIcon, ScreenIcon } from "../chats/icons";
import { cx } from "../chats/cx";

/**
 * A recording as the chat shows it (ENG-1015): a card in the transcript where it was started and a
 * row in the Context rail; each opens its package over the chat (`package-timeline.tsx`). The
 * interview itself is the island's; here a recording is looked at, never driven.
 */

/** The island's window: one per browser, so a second Open brings back the same one. */
const ISLAND_WINDOW = "abacor-island";
export const islandHref = (recordingId: string) =>
  `/island?recording=${encodeURIComponent(recordingId)}`;

/**
 * Opens the island. The desktop app gives it a floating window of its own, over every other app
 * (ENG-1016). The sample build in a browser opens a popup instead, and the card's Open island link
 * is there for when the popup is blocked.
 */
export function openIsland(recordingId: string, desktop?: unknown) {
  const show = desktopShowIsland(desktop);
  if (show) show(recordingId);
  else window.open(islandHref(recordingId), ISLAND_WINDOW, "popup,width=560,height=420");
}

const STATE_WORDS: Record<RecordingState, string> = {
  live: "Recording now",
  paused: "Paused",
  finished: "Finished",
  stopped: "Stopped at the hour limit",
};

/** What a recording is called: what the person said they would show, as a title starts. */
export const recordingTitle = (recording: Pick<RecordingView, "intent">) => {
  const intent = recording.intent?.trim();
  return intent ? intent.charAt(0).toUpperCase() + intent.slice(1) : "Screen recording";
};

/** A recorded length as a clock: "9:33", or "1:02:05" past an hour. */
function lengthClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, "0");
  return h > 0 ? `${String(h)}:${String(m).padStart(2, "0")}:${sec}` : `${String(m)}:${sec}`;
}

/**
 * How a recording stands, from its tape (the one the chat already follows while it is going): how
 * long was recorded, paused time left out, and how many of Abacor's questions were answered and
 * are still open. Null until the tape is read; a tape that failed shows the card without them.
 */
function useStanding(scope: string, recording: RecordingView) {
  const tape = useQuery(recordingTapeQueryOptions(scope, recording));
  return useMemo(() => {
    if (!tape.data) return null;
    const session = replay(tape.data);
    return {
      recordedMs: session.liveMs,
      answered: session.questions.filter((q) => q.state === "answered" || q.state === "closed")
        .length,
      open: openQuestions(session).length,
    };
  }, [tape.data]);
}

function StateLine({
  recording,
  standing,
  now,
}: {
  recording: RecordingView;
  standing: ReturnType<typeof useStanding>;
  now: Date;
}) {
  const started = new Date(recording.createdAt);
  const ended = recording.endedAt === null ? null : new Date(recording.endedAt);
  const going = isGoing(recording.state);
  return (
    <span className="flex items-center gap-1.5 text-xs/4 text-muted-foreground">
      {isGoing(recording.state) && (
        <span
          aria-hidden="true"
          className={cx(
            "size-1.5 shrink-0 rounded-full",
            recording.state === "live" ? "bg-(--live)" : "bg-muted-foreground",
          )}
        />
      )}
      <span className="truncate">
        {STATE_WORDS[recording.state]} · {timeLabel(ended ?? started, now)}
        {/* A going recording's length keeps changing; the island has its clock. */}
        {standing && !going && standing.recordedMs > 0 && ` · ${lengthClock(standing.recordedMs)}`}
        {standing && standing.answered + standing.open > 0 && (
          <>
            {` · ${String(standing.answered)} answered`}
            {standing.open > 0 && (
              <>
                {", "}
                {/* Once it ends, what is left waits for the chat rather than for the island. */}
                <span className="text-(--attention-ink)">
                  {standing.open} {going ? "open" : "unanswered"}
                </span>
              </>
            )}
          </>
        )}
      </span>
    </span>
  );
}

/** How recently a recording started for its card to arrive with motion rather than just be there. */
const ARRIVING_MS = 15_000;

/** How long a deleted recording's card takes to fold away (`.recording-leave`). */
export const RECORDING_LEAVE_MS = 320;

/** The card in the transcript. While the recording is going, it reopens the island. */
export function RecordingCard({
  scope,
  recording,
  now,
  leaving = false,
  onOpen,
}: {
  /** The viewer's scope, which reads the recording's tape for how it stands. */
  scope: string;
  recording: RecordingView;
  now: Date;
  /** It was just deleted: it folds its room away before the chat lets go of it. */
  leaving?: boolean;
  onOpen: (recordingId: string) => void;
}) {
  const going = isGoing(recording.state);
  const standing = useStanding(scope, recording);
  // Decided once, when the card first draws: a recording started a moment ago arrives, one that
  // was already there when the chat opened does not.
  const [arriving] = useState(() => Date.now() - Date.parse(recording.createdAt) < ARRIVING_MS);
  const card = (
    <article
      aria-label={`Recording: ${recordingTitle(recording)}`}
      className="flex items-center gap-3 rounded-[10px] border border-border bg-card p-3 shadow-[0_1px_2px_#1c1b190a]"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground">
        <ScreenIcon size={14} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[13px]/4.5 font-medium text-foreground">
          {recordingTitle(recording)}
        </span>
        <StateLine recording={recording} standing={standing} now={now} />
      </span>
      {/* The island goes with the recording: only where one can be made. */}
      {going && canRecordHere() && (
        <a
          href={islandHref(recording.id)}
          target={ISLAND_WINDOW}
          className={buttonVariants({ variant: "outline", size: "sm" })}
          onClick={(event) => {
            event.preventDefault();
            openIsland(recording.id);
          }}
        >
          Open island
        </a>
      )}
      <Button variant="ghost" size="sm" onClick={() => onOpen(recording.id)}>
        View
        <ChevronRightIcon />
      </Button>
    </article>
  );
  // The inner box has no padding of its own, so the room can fold all the way to nothing.
  if (leaving) {
    return (
      <div className="recording-leave">
        <div>{card}</div>
      </div>
    );
  }
  return arriving ? (
    <div className="recording-arrive">
      <div>{card}</div>
    </div>
  ) : (
    card
  );
}

/** The chat's recordings in the Context rail. Empty draws nothing. */
export function RecordingRows({
  recordings,
  now,
  onOpen,
}: {
  recordings: readonly RecordingView[];
  now: Date;
  onOpen: (recordingId: string) => void;
}) {
  if (recordings.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
      {recordings.map((recording) => (
        <li key={recording.id}>
          <button type="button" className="rail-file" onClick={() => onOpen(recording.id)}>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-[5px] border border-border bg-card text-muted-foreground">
              <ScreenIcon size={12} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[13px]/4.5 text-foreground">
                {recordingTitle(recording)}
              </span>
              <span className="truncate text-xs/4 text-muted-foreground">
                {STATE_WORDS[recording.state]}
              </span>
            </span>
            <span className="font-mono text-[10.5px]/3.5 tracking-[0.04em] text-muted-foreground uppercase">
              {hitTimeLabel(new Date(recording.createdAt), now)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
